// Idempotent, promise-caching loader for external <script> tags. Guarantees a
// given script is fetched at most once per page and lets callers await its
// availability before touching the global it defines, so render-blocking
// third-party SDKs can be pulled in on demand instead of in the document head.

const inFlight = new Map<string, Promise<void>>();

// A stalled request fires neither load nor error; without this ceiling the
// cached promise would stay pending forever and every later caller would await
// the same dead entry. Reject on timeout so the entry is dropped and retryable.
const DEFAULT_TIMEOUT_MS = 15_000;

export default function loadScript(
  src: string,
  { timeoutMs = DEFAULT_TIMEOUT_MS }: { timeoutMs?: number } = {}
): Promise<void> {
  const cached = inFlight.get(src);
  if (cached) {
    return cached;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;

    const timer = setTimeout(() => {
      // Drop the cache entry and node so a later caller can retry the fetch.
      inFlight.delete(src);
      script.remove();
      reject(new Error(`Timed out loading script: ${src}`));
    }, timeoutMs);

    script.addEventListener("load", () => {
      clearTimeout(timer);
      resolve();
    });
    script.addEventListener("error", () => {
      clearTimeout(timer);
      inFlight.delete(src);
      script.remove();
      reject(new Error(`Failed to load script: ${src}`));
    });

    document.head.appendChild(script);
  });

  inFlight.set(src, promise);
  return promise;
}
